
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, Input, OnChanges, OnInit, signal, SimpleChanges, ViewChild } from '@angular/core';
import { ChatCliRepService } from './service/chat-cli-rep.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ChatHeaderComponent } from './components/chat-header.component';
import { ChatMessagesListComponent } from './components/chat-messages-list.component';
import { ChatInputComponent } from './components/chat-input.component';

import { SocketService } from '../../../../../core/service/socket/socket.service';
import { Message, MessageCliRep } from './interfaces/chat-clip-rep.interface';
import { AuthService } from '../../../../../core/service/auth.service';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'chat-cli-rep',
  imports: [
    ChatHeaderComponent,
    ChatMessagesListComponent,
    ChatInputComponent
  ],
  templateUrl: './chat-cli-rep.component.html',
  styleUrl: './chat-cli-rep.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatCliRepComponent implements OnInit, OnChanges {
  orderID: number = 0;
  @Input() orderId: number = 0;
  @Input() picture_delivery: string = '';
  typing = signal<boolean>(false);
  messageHistory = signal<(Message[])>([]);
  private destroy$ = new Subject<void>();
  receiverId = signal(0);
  repartidor = signal('');
  @ViewChild(ChatMessagesListComponent) chatMessagesListComponent!: ChatMessagesListComponent;

  constructor(
    private chatCliRepService: ChatCliRepService,
    private route: ActivatedRoute,
    private sockerService: SocketService,
    public authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    // Removido el effect ya que ahora joinChat se llama directamente en ngOnInit
  }

  ngOnInit(): void {
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.orderID = +params['id'];
      // console.log('id de la orden', this.orderID);

      // Unirse al chat primero
      this.chatCliRepService.joinChat(this.orderID);

      // Esperar un poco antes de suscribirse al typing para asegurar que se unió a la sala
      setTimeout(() => {
        // Suscribirse al evento de typing después de unirse a la sala
        this.chatCliRepService.isTyping().pipe(
          takeUntil(this.destroy$)
        ).subscribe((data) => {
          // console.log('Estado de typing recibido:', data);
          // Solo mostrar el indicador de typing si NO es el usuario actual
          const currentUser = this.authService.getUser();
          if (currentUser && data.userId !== currentUser.ua_id) {
            this.typing.set(data.isTyping);
            this.cdr.detectChanges();
          }
        });
      }, 100);
    });

    this.chatCliRepService.getChatHistory(this.orderID).subscribe((data) => {
      // console.log('Cargando historial inicial:', data.messages);
      // Forzar la actualización de todos los signals
      this.messageHistory.set([...data.messages]);
      this.receiverId.set(data.chatInfo.repaartidor._id);
      this.repartidor.set(data.chatInfo.repaartidor.name);
      this.cdr.detectChanges(); // Forzar detección de cambios

      // Scroll automático al cargar mensajes iniciales
      setTimeout(() => {
        if (this.chatMessagesListComponent) {
          this.chatMessagesListComponent.markForScrollToBottom();
        }
      }, 100);
    })

    // Escuchar nuevos mensajes entrantes
    this.chatCliRepService.onNewMessage().pipe(
      takeUntil(this.destroy$)
    ).subscribe((newMessage: MessageCliRep) => {
      // console.log('Nuevo mensaje recibido:', newMessage);
      // Refrescar el historial de chat para obtener el mensaje actualizado
      this.chatCliRepService.getChatHistory(this.orderID).subscribe((data) => {
        // console.log('Actualizando messageHistory desde mensaje entrante:', data.messages);
        // Forzar la actualización del signal
        this.messageHistory.set([...data.messages]);
        this.cdr.detectChanges(); // Forzar detección de cambios

        // Scroll automático cuando llega un nuevo mensaje
        setTimeout(() => {
          if (this.chatMessagesListComponent) {
            this.chatMessagesListComponent.markForScrollToBottom();
          }
        }, 50);
      });
    });

    // console.log('mensajes de historial', this.messageHistory());
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderId']) {
      this.orderID = changes['orderId'].currentValue;
      // console.log('el id de la orden en el chat es', this.orderID);

    }
    if (changes['picture_delivery']) {
      this.picture_delivery = changes['picture_delivery'].currentValue;
      // console.log('la foto del repartidor es', this.picture_delivery);
    }
  }

  onSendMessage(messageData: { text: string, image?: string }) {
    console.log('Mensaje enviado:', messageData);

    if (messageData.text && messageData.text.trim()) {
      const textMessage: MessageCliRep = {
        senderId: this.authService.getUser()?.ua_id || 0,
        senderUsername: this.authService.getUser()?.ua_name || '',
        receiverId: this.receiverId(),
        orderId: this.orderID.toString(),
        content: messageData.text,
        type: 'TEXT',
        timestamp: new Date(),
        isClient: true
      };

      this.onMessageCliRep({ data: textMessage });
    }
  }

  onFileSelected(file: File): void {
    const messageData: Partial<MessageCliRep> = {
      senderId: this.authService.getUser()?.ua_id || 0,
      senderUsername: this.authService.getUser()?.ua_name || '',
      receiverId: this.receiverId(),
      orderId: this.orderID.toString(),
      isClient: true
    };

    this.chatCliRepService.sendImageMessage(file, messageData).subscribe({
      next: () => {
        // console.log('Imagen enviada exitosamente');
        // Refrescar el historial para mostrar la imagen
        setTimeout(() => {
          this.chatCliRepService.getChatHistory(this.orderID).subscribe((data) => {
            this.messageHistory.set([...data.messages]);
            this.cdr.detectChanges();

            // Scroll automático
            setTimeout(() => {
              if (this.chatMessagesListComponent) {
                this.chatMessagesListComponent.markForScrollToBottom();
              }
            }, 100);
          });
        }, 500);
      },
      error: (error) => {
        console.error('Error enviando imagen:', error);
      }
    });
  }

  onTyping(typingData: { senderId: number, receiverId: number, isTyping: boolean }) {
    this.chatCliRepService.Typing({
      ...typingData,
      orderId: this.orderID
    });
  }
  onMessageCliRep(eventData: { data: MessageCliRep }) {
    console.log('Mensaje recibido en onMessageCliRep:', eventData);
    const baseImg = environment.socketUrl;
    // Agregar el mensaje inmediatamente a la vista de forma optimista
    const currentMessages = this.messageHistory();
    const optimisticMessage: Message = {
      sender: eventData.data.senderUsername,
      senderId: eventData.data.senderId,
      content: eventData.data.content || '',
      type: eventData.data.type,
      timestamp: eventData.data.timestamp,
      metadata: {
        fileUrl: baseImg + eventData.data.fileUrl || '',
        fileName: eventData.data.fileName || '',
        fileSize: eventData.data.fileSize || 0,
        mimeType: eventData.data.mimeType,
        _id: 'temp-' + Date.now()
      }
    };

    // Actualizar la vista inmediatamente
    this.messageHistory.set([...currentMessages, optimisticMessage]);
    this.cdr.detectChanges();

    // Scroll inmediato
    setTimeout(() => {
      if (this.chatMessagesListComponent) {
        this.chatMessagesListComponent.markForScrollToBottom();
      }
    }, 10);

    // Enviar el mensaje
    this.chatCliRepService.sendMessage(eventData.data);

    // Refrescar el historial después de un breve delay para obtener el mensaje con ID real
    setTimeout(() => {
      this.chatCliRepService.getChatHistory(this.orderID).subscribe((data) => {
        // console.log('Actualizando messageHistory con:', data.messages);
        // Actualizar con los datos reales del servidor
        this.messageHistory.set([...data.messages]);
        this.cdr.detectChanges();
      });
    }, 500);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
